import ICita from "../../../../types/Cita";
import { createTheme } from '@mui/material/styles';
import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import ILog from "../../../../types/Log";
import LogService from "../../../../services/LogService";
import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Grid, IconButton, Paper, Snackbar, Tooltip, Typography, Alert, CircularProgress, TextField } from "@mui/material";
import * as constantes from "../../../../common/Constantes";
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import EditIcon from '@mui/icons-material/Edit';
import EmailIcon from '@mui/icons-material/Email';
import IPaciente from "../../../../types/Paciente";
import DocUtils from "../../../../utils/Doc";
import Utilities from "../../../../common/Utilities";
import IAlert from "../../../../types/Alert";
import AgendaService from "../../../../services/AgendaService";
import ConfigUtils from "../../../../utils/Config";
import FacturarCitaDialog from "../../../facturacion/facturarCita/FacturarCitaDialog";
import EstadoFacturacionIcon, { EstadoFacturacion, getEstadoFacturacion } from "../../../facturacion/common/EstadoFacturacionIcon";
import PacientesUtils from "../../../../utils/Pacientes";

export type ICitaConFacturacion = ICita & {
  estadoFacturacion?: EstadoFacturacion;
};

type CitaPasadaProps = {
    cita: ICitaConFacturacion;
    paciente: IPaciente;
    esMenor: boolean;
}

const CitaPasada = (props: CitaPasadaProps) => {
    const theme = createTheme();
    const history = useNavigate();

    const [alert, setAlert] = useState<IAlert>({ open: false, severity: 'info', msg: '' });
    const [loading, setLoading] = useState<boolean>(false);
    const [openDialog, setOpenDialog] = useState<boolean>(false);
    const asistenciaDoc = ConfigUtils.getAsistenciaDoc();
    const sanitarios = ConfigUtils.getSanitarios();
    const servicios = ConfigUtils.getServicios();
    const metodosPago = ConfigUtils.getMetodosPago();

    const [verMas, setVerMas] = useState(false);
    const [justificanteSent, setJustificanteSent] = useState(props.cita.justificanteSent);
    const [horaInicio, setHoraInicio] = useState(
      new Date(props.cita.start).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    );
    
    const [horaFin, setHoraFin] = useState(
      new Date(props.cita.end).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    );

    const [modalFacturarCita, setModalFacturarCita] = useState(false);

    const [estadoFacturacion, setEstadoFacturacion] = useState<EstadoFacturacion>(
        props.cita.estadoFacturacion ?? EstadoFacturacion.SIN_FACTURA
    );

    useEffect(() => {
        setEstadoFacturacion(props.cita.estadoFacturacion ?? EstadoFacturacion.SIN_FACTURA);
    }, [props.cita.estadoFacturacion]);

    const userRole: number = ConfigUtils.getUserRole();

    const handleCloseAlert = (event?: React.SyntheticEvent | Event, reason?: string) => {
        if (reason === 'clickaway') {
          return;
        }
        setAlert({ open: false, severity: 'info', msg: '' });
    };

    const sendJustificante = () => {
        if (props.esMenor) {
            if (Utilities.isNullEmptyUndefined(props.paciente.DNItutor)) {
                setOpenDialog(false);
                setAlert({ open: true, severity: 'warning', msg: 'El tutor del paciente no tiene DNI guardado' });
                return;
            }
        } else {
            if (Utilities.isNullEmptyUndefined(props.paciente.DNI)) {
                setOpenDialog(false);
                setAlert({ open: true, severity: 'warning', msg: 'El paciente no tiene DNI guardado' });
                return;
            }
        }
    
        setLoading(true);

        const citaModificada = { ...props.cita };
        const fechaInicio = new Date(props.cita.start);
        const fechaFin = new Date(props.cita.end);

        // Reemplazar horas con las introducidas en los inputs
        const [hInicio, mInicio] = horaInicio.split(":");
        const [hFin, mFin] = horaFin.split(":");

        fechaInicio.setHours(parseInt(hInicio), parseInt(mInicio));
        fechaFin.setHours(parseInt(hFin), parseInt(mFin));

        citaModificada.start = fechaInicio;
        citaModificada.end = fechaFin;

        const justificante = DocUtils.formatJustificanteCita(props.paciente, citaModificada, props.esMenor);
    
        AgendaService.sendJustificante({ email: props.paciente.email, doc: justificante, citaId: props.cita._id, pacienteNombre: props.paciente.nombre })
        .then(response => {
            setAlert({ open: true, severity: 'success', msg: response.data.message });
            setJustificanteSent(true);
        })
        .catch (e => {
            if (e.response.status === 401) {
                localStorage.clear();
                history(constantes.R_HOME);
            } else {
                const error: ILog = { method: 'CitaActual.Component.sendJustificante', level: 'error', message: e.message, meta: e.response };
                LogService.logError(error);
                setAlert({ open: true, severity: 'error', msg: e.response.data.message });
            }
        })
        .finally(() => {
            setLoading(false);
            setOpenDialog(false);
        });
    };

    const facturacion = servicios.length > 0 && metodosPago.length > 0;
    const { isDisabledEmail, tooltipMsg } = PacientesUtils.canSendEmail(
      loading,
      props.paciente.authEmail,
      props.paciente.email,
      justificanteSent
        ? "Volver a enviar justificante de asistencia"
        : "Enviar justificante de asistencia",
      true,
      asistenciaDoc,
    );

    try {
        return (
        <>
            <Paper elevation={2} sx={{ marginTop: theme.spacing(1), marginBottom: theme.spacing(1), padding: theme.spacing(2), backgroundColor: props.cita.asistencia === false ? '#ffbaba' : '#fff', borderLeft: `5px solid ${sanitarios.filter((s) => s.internalID === props.cita.especialistaId)[0] == undefined ? 'grey' : sanitarios.filter((s) => s.internalID === props.cita.especialistaId)[0]?.color}` }} key={props.cita._id}>
                <Grid container sx={{ minWidth: '75vw', maxWidth: '75vw' }}>
                    <Grid container alignItems="center" sx={{ minWidth: '75vw', maxWidth: '75vw' }}>

                        <Grid container alignItems="center" sx={{ minWidth: '75vw', maxWidth: '75vw' }}>
                            {/* Botón VER MÁS / VER MENOS */}
                            {userRole !== constantes.ROLES.SECRETARIO && props.cita.asistencia !== false && (
                                <Grid item>
                                    <Tooltip title={verMas ? "VER MENOS INFORMACIÓN" : "VER MÁS INFORMACIÓN"}>
                                        <IconButton onClick={() => setVerMas(!verMas)}>
                                            {verMas ? <RemoveIcon /> : <AddIcon />}
                                        </IconButton>
                                    </Tooltip>
                                </Grid>
                            )}

                            {/* Texto de la cita */}
                            <Grid item xs>
                                <div style={{ display: 'flex', alignItems: 'center', marginLeft: '8px' }}>
                                    <Typography variant="h5">{new Date(props.cita.start).getDate()}&nbsp;</Typography>
                                    <Typography variant="body2">{`${constantes.monthNames[new Date(props.cita.start).getMonth()].toUpperCase()} ${new Date(props.cita.start).getFullYear()}`}</Typography>
                                </div>
                            </Grid>
                        
                            {/* Botones a la derecha */}
                            <Grid item>
                                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                                    {/* Botón EDITAR CITA */}
                                    {userRole !== constantes.ROLES.SECRETARIO && props.cita.asistencia !== false && (
                                        <Link to={constantes.R_CITA} state={{ cita: props.cita, paciente: props.paciente }} style={{ textDecoration: 'none' }}>
                                            <Tooltip title="EDITAR CITA">
                                                <IconButton>
                                                    <EditIcon style={{ cursor: 'pointer', color: 'grey' }} />
                                                </IconButton>
                                            </Tooltip>
                                        </Link>
                                    )}

                                    {/* Botón ENVIAR JUSTIFICANTE */}
                                    <Tooltip title={tooltipMsg}>
                                      <span>
                                        <IconButton
                                          onClick={() => setOpenDialog(true)}
                                          disabled={isDisabledEmail}
                                        >
                                          <EmailIcon color={justificanteSent ? "success" : "inherit"} />
                                        </IconButton>
                                      </span>
                                    </Tooltip>

                                    {/* Botón FACTURAR CITA */}
                                    {facturacion && (
                                    <EstadoFacturacionIcon
                                      estado={estadoFacturacion}
                                      onClick={() => setModalFacturarCita(true)}
                                    />
                                    )}
                                </div>
                            </Grid>
                        </Grid>
                        
                    </Grid>

                    {verMas &&
                        <>
                            <Grid item md={12} xs={10}>
                                <Typography sx={{ fontWeight: 'bold' }}>Motivo: </Typography><Typography>{props.cita.motivo}</Typography>
                            </Grid>
                            <Grid item md={12} xs={10}>
                                <Typography sx={{ fontWeight: 'bold' }}>Valoración: </Typography><Typography>{props.cita.valoracion}</Typography>
                            </Grid>
                            <Grid item md={12} xs={10}>
                                <Typography sx={{ fontWeight: 'bold' }}>Razonamiento: </Typography><Typography>{props.cita.razonamiento}</Typography>
                            </Grid>
                            <Grid item md={12} xs={10}>
                                <Typography sx={{ fontWeight: 'bold' }}>Tratamiento: </Typography><Typography>{props.cita.tratamiento}</Typography>
                            </Grid>
                        </>
                    }


                </Grid>
            </Paper>

            {/* ENVIAR JUSTIFICANTE */}
            <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
              <DialogTitle>ENVIAR JUSTIFICANTE POR EMAIL</DialogTitle>
              <DialogContent>
                <DialogContentText>
                  Confirme las horas que se incluirán en el justificante de asistencia.
                </DialogContentText>            
            
                <Grid container spacing={2} sx={{ mt: 2 }}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Hora inicio"
                      type="time"
                      value={horaInicio}
                      onChange={(e) => setHoraInicio(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Hora fin"
                      type="time"
                      value={horaFin}
                      onChange={(e) => setHoraFin(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                </Grid>
              </DialogContent>          
            
              <DialogActions>
                {loading ? (
                  <Grid container>
                    <CircularProgress style={{ margin: '10px auto' }} />
                  </Grid>
                ) : (
                  <>
                    <Button
                      onClick={sendJustificante}
                      color="primary"
                    >
                      ENVIAR
                    </Button>
                    <Button onClick={() => setOpenDialog(false)} color="primary" autoFocus>
                      CANCELAR
                    </Button>
                  </>
                )}
              </DialogActions>
            </Dialog>

            {/* FACTURAR CITA */}
            {modalFacturarCita && 
                <FacturarCitaDialog 
                    open={modalFacturarCita} 
                    tipo="activa"
                    onClose={() => setModalFacturarCita(false)} 
                    citaId={props.cita._id!}
                    pacienteId={props.paciente._id} 
                    onServicioActualizado={(updated) => {
                      setEstadoFacturacion(updated.estado);
                      if (!updated.sinAviso) setAlert({ open: true, severity: "success", msg: "Factura actualizada correctamente" });
                    }}
                    onFacturaRectificada={(factura) => {
                      setEstadoFacturacion(getEstadoFacturacion(factura.estado, factura.estadoPago));
                      setAlert({ open: true, severity: "success", msg: "Factura rectificada correctamente" });
                    }}
                    onRectificativaActualizada={(updated) => {
                      setEstadoFacturacion(updated.estado);
                      setAlert({ open: true, severity: "success", msg: "Factura actualizada correctamente" });
                    }}
                    onAnularFactura={() => {
                        setEstadoFacturacion(EstadoFacturacion.ANULADA);
                    }}
                    onEntidadCambiada={(_facturaAnuladaId, facturaNueva) => {
                        setEstadoFacturacion(getEstadoFacturacion(facturaNueva.estado, facturaNueva.estadoPago));
                    }}
                />
            }

            <Snackbar open={alert.open} autoHideDuration={6000} onClose={handleCloseAlert}>
                <Alert variant="filled" onClose={handleCloseAlert} severity={alert.severity} sx={{ width: '100%' }}>{alert.msg}</Alert>
            </Snackbar>
        </>
        );
    } catch (e: any) {
        const error: ILog = { method: 'CitaPasada.Component', level: 'error', message: e.message, meta: e.message };
        LogService.logError(error);
        return (
            <Alert severity="error">{e.message}</Alert>
        );
    }
};

export default CitaPasada;